import { Injectable } from '@angular/core';
import {
  TREQUEST_EDIT_LEARNING_CURVE_ORDER_EFFICIENCY,
  TREQUEST_EDIT_ORDER_DETAILS_HEADER,
  TREQUEST_DEFAULT_COUNT_EDIT_ORDER_DETAILS,
  TREQUEST_ORDER_DETAILS_HEADER,
  TREQUEST_ORDER_DETAILS_LEARNING_CURVE,
  TREQUEST_ORDER_DETAILS_PLANNING_CARD,
  TREQUEST_RESET_LL_LC_BY_FACTORY_OTL,
  TREQUEST_GET_OPTIMAL_PLAN_BY_OPL_ID,
  TREQUEST_LOAD_OPL_STRIPS,
  TREQUEST_CLOSE_OPL_BY_OPL_ID,
  TREQUEST_ORDER_DETAILS_DELETE_ALLOCATION_BY_FACTORY,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import * as orderDetailsActions from './order-details.actions';
import * as orderDetailsSelectors from './order-details.selectors';
import { TLiftedFormModel } from '@monorepo/ui';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class OrderDetailsFacade {
  constructor(
    private store: Store<TPlani8State>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private plani8DataService: Plani8DataService,
  ) {}

  setIsSyncEnabled(syncEnabled: boolean) {
    this.store.dispatch(new orderDetailsActions.SetIsSyncEnabled(syncEnabled));
  }

  getIsSyncEnabled() {
    return this.store.pipe(select(orderDetailsSelectors.getIsSyncEnabled));
  }

  setShowPlanningCardLoader(showLoader: boolean) {
    this.store.dispatch(new orderDetailsActions.SetPlanningCardLoader(showLoader));
  }

  fetchPlanningcard(
    payload: TREQUEST_ORDER_DETAILS_PLANNING_CARD,
    destination: 'LL_BOARD' | 'ORDER_DETAILS' = 'ORDER_DETAILS',
  ) {
    this.store.dispatch(new orderDetailsActions.GetPlanningCard(payload, destination));
  }

  getShowPlanningCardLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowPlanningCardLoader));
  }

  getPlanningCard() {
    return this.store.pipe(select(orderDetailsSelectors.getPlanningCardInfo));
  }

  clearPlanningCardDetails() {
    this.store.dispatch(new orderDetailsActions.ClearPlanningCardDetails());
  }

  setLeaningCurveLoader(showLoader: boolean) {
    this.store.dispatch(new orderDetailsActions.SetLearningCurveCardLoader(showLoader));
  }

  fetchLearningCurveCard(payload: TREQUEST_ORDER_DETAILS_LEARNING_CURVE) {
    this.store.dispatch(new orderDetailsActions.GetLearningCurveCard(payload));
  }

  getShowLearningCurveCardLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowLearningCurveCardLoader));
  }

  clearLearningCurveCardDetails() {
    this.store.dispatch(new orderDetailsActions.ClearlearningCurveCardDetails());
  }

  getLearningCurveCard() {
    return this.store.pipe(select(orderDetailsSelectors.getLearningCurveCardInfo));
  }

  setHeaderLoader(val: boolean) {
    return this.store.dispatch(new orderDetailsActions.SetHeaderLoader(val));
  }

  fetchHeader(payload: TREQUEST_ORDER_DETAILS_HEADER) {
    return this.store.dispatch(new orderDetailsActions.GetHeader(payload));
  }

  getShowHeaderDetailsLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowHeaderDetailsLoader));
  }

  getHeader() {
    return this.store.pipe(select(orderDetailsSelectors.getHeaderInfo));
  }

  editHeader(payload: TREQUEST_EDIT_ORDER_DETAILS_HEADER) {
    return this.store.dispatch(new orderDetailsActions.EditHeader(payload));
  }

  editDefaultCount(payload: TREQUEST_DEFAULT_COUNT_EDIT_ORDER_DETAILS) {
    return this.store.dispatch(new orderDetailsActions.EditDefaultCount(payload));
  }

  async markOrderNotRequired(otlId: string) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.markOrderNotRequired(otlId).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  setShowAllLearningCurvesLoader(val: boolean) {
    return this.store.dispatch(new orderDetailsActions.SetShowAllLearningCurvesLoader(val));
  }

  fetchAllLearningCurves() {
    return this.store.dispatch(new orderDetailsActions.GetAllLearningCurves());
  }

  getAllLearningCurves() {
    return this.store.pipe(select(orderDetailsSelectors.getAllLearningCurvesInfo));
  }

  getShowAllLearningCurvesLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowAllLearningCurvesLoader));
  }

  fetchViewLearningCurveModal(learningCurveId: string) {
    return this.store.dispatch(new orderDetailsActions.GetViewLearningCurveModal(learningCurveId));
  }

  getViewLearningCurveModal() {
    return this.store.pipe(select(orderDetailsSelectors.getViewLearningCurveInfo));
  }

  editLearningCurveOrOrderEfficiency(
    editPayload: TREQUEST_EDIT_LEARNING_CURVE_ORDER_EFFICIENCY,
    fetchPayload: TREQUEST_ORDER_DETAILS_LEARNING_CURVE,
  ) {
    return this.store.dispatch(new orderDetailsActions.EditLearningCurveOrderEfficiency(editPayload, fetchPayload));
  }

  setPoDetailsLoader(showLoader: boolean) {
    this.store.dispatch(new orderDetailsActions.SetPoDetailsLoader(showLoader));
  }

  clearPoCardDetails() {
    this.store.dispatch(new orderDetailsActions.ClearPoCardDetails());
  }

  fetchPoDetailscard(poDetailsReq: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.GetPoDetailsCard(poDetailsReq));
  }

  addUpdatePoDetailsOrder(poDetailsOrderUpdateReq: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.AddUpdatePoDetailsOrder(poDetailsOrderUpdateReq));
  }

  deletePoDetailsOrder(poDetailsOrderDeleteReq: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.DeletePoDetailsOrder(poDetailsOrderDeleteReq));
  }

  getShowPoDetailsLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowPoDetailsLoader));
  }

  getPoDetailsCard() {
    return this.store.pipe(select(orderDetailsSelectors.getPoDetailsCardInfo));
  }

  fetchPoColorSizeQtyInfo(poColorSizeDetails: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.GetPoColorSizeQtyDetails(poColorSizeDetails));
  }

  updatePoColorQtyByRatio(colorQtyByRatioUpdateReq: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.UpdatePoColorQtyByRatio(colorQtyByRatioUpdateReq));
  }

  updatePoColorSizeQtyManually(colorSizeQtyManualUpdateReq: TLiftedFormModel['payload']) {
    this.store.dispatch(new orderDetailsActions.UpdatePoColorSizeQtyManually(colorSizeQtyManualUpdateReq));
  }

  getPoColorSizeQtyInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getPoColorSizeQtyInfo));
  }

  setOrderAllocationLoader(val: boolean) {
    this.store.dispatch(new orderDetailsActions.SetOrderAllocationLoader(val));
  }

  fetchAllocationCardInfo(otlId: string) {
    this.store.dispatch(new orderDetailsActions.GetAllocationCardDetails(otlId));
  }

  clearAllocationCardInfo() {
    this.store.dispatch(new orderDetailsActions.ClearAllocationCardDetails());
  }

  getAllocationCardInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getAllocationCardInfo));
  }

  getShowOrderAllocationLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowOrderAllocationLoader));
  }

  async deleteAllocationByFactory(payload: TREQUEST_ORDER_DETAILS_DELETE_ALLOCATION_BY_FACTORY): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.deleteAllocationByFactory(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  fetchLinewisePlannedQtyCardInfo(otlId: string) {
    this.store.dispatch(new orderDetailsActions.GetLinewisePlannedQtyCardDetails(otlId));
  }
  getLinewisePlannedQtyCardInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getLinewisePlannedQtyCardInfo));
  }
  setLinewisePlannedQtyLoader(val: boolean) {
    this.store.dispatch(new orderDetailsActions.SetLinewisePlannedQtyLoader(val));
  }
  getShowLinewisePlannedQtyLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowLinewisePlannedQtyLoader));
  }

  fetchOrderTimelineCardInfo(otlId: string) {
    this.store.dispatch(new orderDetailsActions.GetOrderTimelineCardDetails(otlId));
  }
  getOrderTimelineCardInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getOrderTimelineCardInfo));
  }
  setOrderTimelineLoader(val: boolean) {
    this.store.dispatch(new orderDetailsActions.SetOrderTimelineLoader(val));
  }
  getShowOrderTimelineLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowOrderTimelineLoader));
  }

  setOperationBreakdownDetailsLoader(val: boolean) {
    this.store.dispatch(new orderDetailsActions.SetOperationBreakdownDetailsLoader(val));
  }

  fetchOperationBreakdownDetails(otlId: string) {
    this.store.dispatch(new orderDetailsActions.GetOperationBreakdownDetails(otlId));
  }

  getOperationBreakdownDetailsInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getObDetailsInfo));
  }

  getShowOperationBreakdownDetailsLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowOperationBreakdownDetailsLoader));
  }

  async getFactoryNames() {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);

    return lastValueFrom(this.plani8DataService.fetchFactoryNames()).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async syncOrderDetails(otlId: string) {
    return lastValueFrom(this.plani8DataService.startOrderDetailsSync(otlId));
  }

  async pollOrderDetailsStatus(otlId: string) {
    return lastValueFrom(this.plani8DataService.getOrderDetailsPollStatus(otlId));
  }

  async resetLearningCurveFromOrderDetails(payload: TREQUEST_RESET_LL_LC_BY_FACTORY_OTL): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.resetLearningCurveFromOrderDetails(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  setProcessMappingDetailsLoader(showLoader: boolean) {
    this.store.dispatch(new orderDetailsActions.SetProcessMappingDetailsLoader(showLoader));
  }
  getProcessMappingDetailsLoader() {
    return this.store.pipe(select(orderDetailsSelectors.getShowProcessMappingDetailsLoader));
  }
  fetchProcessMappingDetailsCardInfo(otlId: string) {
    this.store.dispatch(new orderDetailsActions.FetchProcessMappingDetailsCard(otlId));
  }
  getProcessMappingDetailsCardInfo() {
    return this.store.pipe(select(orderDetailsSelectors.getProcessMappingDetailsCardInfo));
  }
  async getProcessMapsList() {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.fetchAllProcessMaps().pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async getOptimalPlanByOplId(payload: TREQUEST_GET_OPTIMAL_PLAN_BY_OPL_ID) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.getOptimalPlanByOplId(payload).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async loadOplStrips(payload: TREQUEST_LOAD_OPL_STRIPS) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.loadOplStrips(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }

  async closeOpl(payload: TREQUEST_CLOSE_OPL_BY_OPL_ID) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.closeOplByOplId(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetPlanningCardInfo = ReturnType<OrderDetailsFacade['getPlanningCard']>;
export type TGetLearningCurveCardInfo = ReturnType<OrderDetailsFacade['getLearningCurveCard']>;
export type TGetAllLearningCurves = ReturnType<OrderDetailsFacade['getAllLearningCurves']>;
export type TGetViewLearningCurveModal = ReturnType<OrderDetailsFacade['getViewLearningCurveModal']>;
export type TGetHeaderInfo = ReturnType<OrderDetailsFacade['getHeader']>;
export type TGetPoDetailsCardInfo = ReturnType<OrderDetailsFacade['getPoDetailsCard']>;
export type TGetPoColorSizeQtyInfo = ReturnType<OrderDetailsFacade['getPoColorSizeQtyInfo']>;
export type TGetAllocationCardInfo = ReturnType<OrderDetailsFacade['getAllocationCardInfo']>;
export type TGetObDetailsInfo = ReturnType<OrderDetailsFacade['getOperationBreakdownDetailsInfo']>;
